import { useEffect } from 'react';
import {
  DefaultEntityFilters,
  EntityFilter,
  EntityKindPicker,
  EntityListProvider,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { DOC_TYPE_ANNOTATION } from '@internal/plugin-neo4j-techdocs-common';
import { ButtonLink, Flex, Text } from '@backstage/ui';
import { RiAddLine, RiExternalLinkLine } from '@remixicon/react';
import { useRouteRef } from '@backstage/core-plugin-api';
import pluralize from 'pluralize';
import { DocTypeData } from '../types';
import { EntityListDocsGrid } from './EntityListDocsGrid';
import { RecentDocs } from './RecentDocs';
import { createScaffolderSelectedTemplatePageRef } from '../routes';
import styles from './DocsCategoryView.module.css';

type DocTypeFilters = DefaultEntityFilters & {
  view: EntityFilter;
};

/**
 * A single docs category landing page with a table of docs, such as Lego bricks.
 */
export const DocsCategoryView = ({ docType }: { docType: DocTypeData }) => (
  <EntityListProvider>
    <EntityKindPicker hidden />
    <DocTypeFilterPicker docType={docType} />
    <CategoryViewContent docType={docType} />
  </EntityListProvider>
);

class DefaultDocsFilter implements EntityFilter {
  private key: string;
  private entityType: string | undefined;
  private docType: string | undefined;

  constructor(key: string, entityType?: string, docType?: string) {
    this.key = key;
    this.entityType = entityType;
    this.docType = docType;
  }

  toQueryValue(): string {
    return this.key;
  }

  getCatalogFilters(): Record<string, string | symbol | (string | symbol)[]> {
    const filters: Record<string, string | symbol | (string | symbol)[]> = {};
    if (this.entityType) {
      filters[`spec.type`] = this.entityType;
    }
    if (this.docType) {
      filters[`metadata.annotations.${DOC_TYPE_ANNOTATION}`] = this.docType;
    }
    return filters;
  }
}

/**
 * EntityListProvider also writes this filter to `filters[view]` in the URL.
 */
function DocTypeFilterPicker({ docType }: { docType: DocTypeData }) {
  const { updateFilters } = useEntityList<DocTypeFilters>();

  useEffect(() => {
    updateFilters({
      view: new DefaultDocsFilter(
        docType.type,
        docType.entityType,
        docType.type,
      ),
    });
  }, [updateFilters, docType]);

  return null;
}

function CategoryViewContent({ docType }: { docType: DocTypeData }) {
  const getTemplatePath = useRouteRef(createScaffolderSelectedTemplatePageRef);
  const newDocPath = getTemplatePath({
    namespace: 'default',
    templateName: docType.templateName,
  });
  const pluralNoun = pluralize(docType.noun);

  return (
    <Flex direction="column" gap="10">
      <CategoryToolbar docType={docType} newDocPath={newDocPath} />
      <EntityListDocsGrid
        title={`All ${pluralNoun}`}
        errorTitle={`Could not load the ${pluralNoun}`}
        emptyState={
          <Flex align="center" gap="3">
            <Text variant="body-medium">{`No ${pluralNoun} yet.`}</Text>
            <NewDocLink
              noun={docType.noun}
              href={newDocPath}
              variant="secondary"
              size="small"
            />
          </Flex>
        }
      />
      <RecentDocs
        docType={docType.type}
        emptyText={`The ${pluralNoun} that you open show here.`}
      />
    </Flex>
  );
}

function CategoryToolbar({
  docType,
  newDocPath,
}: {
  docType: DocTypeData;
  newDocPath: string;
}) {
  const { icon: Icon } = docType;

  return (
    <div className={styles.toolbar}>
      <Flex direction="column" gap="3" className={styles.about}>
        <Flex align="center" gap="3">
          <Icon size={24} aria-hidden="true" />
          <Text as="h2" variant="body-large" weight="bold">
            {docType.title}
          </Text>
        </Flex>
        <Text
          as="p"
          variant="body-medium"
          weight="bold"
          className={styles.summary}
        >
          {docType.summary}
        </Text>
        <Text as="p" variant="body-medium" color="secondary">
          {docType.description}
        </Text>
      </Flex>
      <NewDocLink
        noun={docType.noun}
        href={newDocPath}
        className={styles.createLink}
      />
    </div>
  );
}

/**
 * The portal theme adds the external-link icon to BUI `Link`, but not
 * `ButtonLink`, so this button supplies it explicitly.
 */
function NewDocLink({
  noun,
  href,
  variant = 'primary',
  size,
  className,
}: {
  noun: string;
  href: string;
  variant?: 'primary' | 'secondary';
  size?: 'small' | 'medium';
  className?: string;
}) {
  return (
    <ButtonLink
      href={href}
      target="_blank"
      variant={variant}
      size={size}
      iconStart={<RiAddLine />}
      iconEnd={<RiExternalLinkLine />}
      className={className}
    >
      {`Create ${noun}`}
    </ButtonLink>
  );
}
